import { useEffect, useState } from "react";
import { Loader2, Pencil, X, RefreshCw, ShieldAlert, Send, CheckCircle2, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import AdminShell from "@/components/admin/AdminShell";

/**
 * Admin panel — rotate/edit third-party API keys (Resend, Cloudinary, …).
 * Sensitive values are masked. Editing writes to DB overrides which win over
 * environment variables on the next request — no redeploy required.
 */
export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null); // key currently being edited
  const [editValue, setEditValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/config/integrations");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = (item) => {
    setEditKey(item.key);
    setEditValue("");
    setShowValue(false);
  };

  const cancelEdit = () => {
    setEditKey(null);
    setEditValue("");
    setShowValue(false);
  };

  const save = async () => {
    if (!editKey) return;
    setSaving(true);
    try {
      const r = await adminApi.put(`/admin/config/integrations/${editKey}`, {
        value: editValue,
      });
      setItems(r.data?.items || []);
      toast.success(`${editKey} updated`);
      cancelEdit();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (key) => {
    if (!window.confirm(`Clear DB override for ${key}? The value will fall back to the environment variable (server-set).`)) return;
    try {
      const r = await adminApi.put(`/admin/config/integrations/${key}`, { value: "" });
      setItems(r.data?.items || []);
      toast.success(`${key} override cleared`);
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    try {
      const r = await adminApi.post("/admin/config/integrations/test/resend");
      toast.success(r.data?.message || "Test email dispatched.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const grouped = {
    Resend: items.filter((i) => i.key.startsWith("RESEND_") || i.key.endsWith("_EMAIL") || i.key === "SENDER_EMAIL" || i.key === "ADMIN_NOTIFICATION_EMAIL"),
    Cloudinary: items.filter((i) => i.key.startsWith("CLOUDINARY_")),
  };

  return (
    <AdminShell title="Integrations">
      <div className="mb-6 flex items-start gap-3 bg-[#FFF8E7] border border-[#F5D68B] p-4">
        <ShieldAlert size={18} className="text-[#8B6D1E] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[#8B6D1E]">
          <strong>Sensitive area.</strong> API keys stored here override the
          server environment variables at runtime. Rotate keys here whenever a
          third-party service changes credentials — no redeploy required.
          Sensitive values are masked.
        </div>
      </div>

      {loading ? (
        <div className="p-16 flex items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Resend group */}
          <IntegrationGroup
            title="Resend · Email delivery"
            description="Transactional email for quote requests, job applications, and contact form submissions. Domain ags-services.ie is verified in Resend."
            items={grouped.Resend}
            editKey={editKey}
            editValue={editValue}
            setEditValue={setEditValue}
            showValue={showValue}
            setShowValue={setShowValue}
            saving={saving}
            onEdit={startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClear={clearOverride}
            extraAction={
              <button
                onClick={sendTestEmail}
                disabled={testing}
                data-testid="test-resend-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#0E2A57] text-[#0E2A57] hover:bg-ags-cream px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-60"
              >
                {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {testing ? "Sending…" : "Send test email"}
              </button>
            }
          />

          {/* Cloudinary group */}
          <IntegrationGroup
            title="Cloudinary · Media hosting"
            description="Image and video uploads from the CMS Media Library are pushed to Cloudinary. Rotate keys here if you regenerate them in the Cloudinary dashboard."
            items={grouped.Cloudinary}
            editKey={editKey}
            editValue={editValue}
            setEditValue={setEditValue}
            showValue={showValue}
            setShowValue={setShowValue}
            saving={saving}
            onEdit={startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClear={clearOverride}
          />
        </div>
      )}
    </AdminShell>
  );
}

function IntegrationGroup({
  title, description, items, extraAction,
  editKey, editValue, setEditValue, showValue, setShowValue, saving,
  onEdit, onSave, onCancel, onClear,
}) {
  return (
    <div className="bg-white border border-ags-hairline">
      <div className="p-5 border-b border-ags-hairline flex items-start justify-between gap-4">
        <div>
          <div className="font-heading font-bold text-lg text-[#0E2A57]">{title}</div>
          <div className="mt-1 text-sm text-ags-text-muted max-w-xl">{description}</div>
        </div>
        {extraAction}
      </div>
      <table className="w-full">
        <tbody>
          {items.map((it) => {
            const isEditing = editKey === it.key;
            return (
              <tr key={it.key} data-testid={`integration-row-${it.key}`} className="border-b border-ags-hairline last:border-0">
                <td className="p-4 w-64 align-top">
                  <div className="font-heading font-bold text-sm text-[#0E2A57]">{it.label}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#94A3B8]">
                    <code>{it.key}</code>
                  </div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[#94A3B8]">
                    source: {it.source} · {it.is_set ? "set" : "empty"}
                  </div>
                </td>
                <td className="p-4">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type={showValue || !it.sensitive ? "text" : "password"}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder={`New ${it.label}`}
                        data-testid={`integration-input-${it.key}`}
                        autoFocus
                        className="flex-1 border border-[#0E2A57] px-3 py-2 text-sm focus:outline-none"
                      />
                      {it.sensitive && (
                        <button
                          type="button"
                          onClick={() => setShowValue((v) => !v)}
                          className="p-2 border border-ags-hairline hover:bg-ags-cream"
                          title={showValue ? "Hide" : "Reveal"}
                        >
                          {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                      <button
                        onClick={onSave}
                        disabled={saving || !editValue.trim()}
                        data-testid={`integration-save-${it.key}`}
                        className="inline-flex items-center gap-2 bg-[#0E2A57] hover:bg-[#050B14] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
                      >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        Save
                      </button>
                      <button
                        onClick={onCancel}
                        className="p-2 border border-ags-hairline hover:bg-ags-cream"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <code className="flex-1 font-mono text-sm text-ags-ink bg-ags-cream px-3 py-2">
                        {it.value || <span className="text-[#94A3B8]">— not set —</span>}
                      </code>
                      <button
                        onClick={() => onEdit(it)}
                        data-testid={`integration-edit-${it.key}`}
                        className="inline-flex items-center gap-2 border border-[#0E2A57] text-[#0E2A57] hover:bg-ags-cream px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
                      >
                        <Pencil size={12} /> Change
                      </button>
                      {it.source === "db" && (
                        <button
                          onClick={() => onClear(it.key)}
                          title="Clear DB override (fall back to env var)"
                          className="p-2 text-ags-text-muted hover:text-red-600 hover:bg-red-50"
                        >
                          <RefreshCw size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
